import {CircularProgress, Grid} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {KeyboardArrowRight} from '@material-ui/icons';
import {useOnSubmitResponse, usePostForm} from 'hooks';
import usePutEndpoint from 'hooks/usePutEndpoint';
import React from 'react';
import {useContext, useEffect, useRef} from 'react';
import {FormProvider} from 'react-hook-form';
import {FormNavigation, FormState} from './context';
import {defaultNext, defaultBack} from './context/FormNavigation';
import {useClearForm, useFormMethods, useFormPersistence} from './hooks';
import {sanitizeData} from './sections/Review/sanitizer';
import {getCurrentState} from './utils/state';

const useStyles = makeStyles(theme => ({
    root: {
        margin: theme.spacing(1),
        flexGrow: 1,
    },
}));

const applyGrid = children => {
    return React.Children.map(children, (child, i) => {
        if (React.isValidElement(child)) {
            return (
                <Grid item key={`child-${i}`}>
                    {child}
                </Grid>
            );
        } else {
            return child;
        }
    });
};

const EndSection = ({
    schema,
    initialValues,
    buttonText,
    // submit data
    sanitize = true,
    sanitizeFunction,
    endpointOverride,
    clearFormOverride,
    method = 'post',
    // handle response
    successText,
    finalAction, // successFunction
    buttonDisabled = false,
    backButtonDisabled = false,
    redirectUrl,
    redirectOnSuccess,
    myAwesomeFunction,

    // section contents
    gridify,
    ...props
}) => {
    const classes = useStyles();
    const {node} = useContext(FormState);
    const {state, updateState} = useFormPersistence();
    const form = useRef(null);
    const defaultClearFormState = useClearForm(node);
    const clearFormState = clearFormOverride ? () => {} : defaultClearFormState;
    const onSubmitResponse = useOnSubmitResponse({
        successText: successText,
        clearFormState: clearFormState,
        successFunction: finalAction,
        redirectUrl: redirectUrl,
        redirectOnSuccess: redirectOnSuccess,
    });
    
    // use the right method, post vs put
    const {
        post: postPost,
        submissionInProgress: postSIP,
        result: postR,
        error: postE,
    } = usePostForm(endpointOverride || node);
    const {
        put,
        submissionInProgress: putSIP,
        result: putR,
        error: putE,
    } = usePutEndpoint(`/api/1/user/${endpointOverride || node}`);
    const [post, submissionInProgress, result, error] =
        method === 'put'
            ? [put, putSIP, putR, putE]
            : [postPost, postSIP, postR, postE];

    const methods = useFormMethods({
        schema: schema,
        initialValues: initialValues,
    });

    const saveAndReturnLatest = () => {
        const data = methods.getValues();
        updateState(data);
        return getCurrentState(`forms[${node}]`);
    };

    const runValidations = async () => {
        if (schema && Object.keys(schema).length) {
            if (await methods.trigger()) {
                return saveAndReturnLatest();
            } else {
                console.debug('validation errors', methods.errors);
                return false;
            }
        } else {
            return state || true;
        }
    };

    const nextBtn = {
        ...defaultNext,
        testHook: 'save-and-enter',
        disabled: submissionInProgress || buttonDisabled,
        label: submissionInProgress ? '' : buttonText || 'Save',
        icon: submissionInProgress ? CircularProgress : KeyboardArrowRight,
        onClick: async () => {
            const newState = await runValidations();
            if (newState) {
                post(
                    sanitize
                        ? sanitizeFunction
                            ? sanitizeFunction(newState)
                            : sanitizeData(newState)
                        : newState
                );
            }
        },
    };

    const backBtn = {
        ...defaultBack,
        disabled: backButtonDisabled,
    };

    const {setNextButton, setBackButton, triggerPost, setTriggerPost} =
        useContext(FormNavigation);

    useEffect(() => {
        setNextButton(nextBtn);
        setBackButton(backBtn);
        // eslint-disable-next-line
    }, [submissionInProgress, buttonDisabled]);

    useEffect(() => {
        if (triggerPost) {
            post(state);
            setTriggerPost(triggerPost => false);
        }
        // eslint-disable-next-line
    }, [triggerPost]);

    useEffect(() => {
        onSubmitResponse({
            result: result,
            error: error,
        });
        if (myAwesomeFunction) {
            myAwesomeFunction(result);
        }
        // eslint-disable-next-line
    }, [error, result]);

    const persistData = data => {
        updateState(data);
    };

    return (
        <FormProvider {...methods}>
            <form
                className={classes.root}
                onSubmit={methods.handleSubmit(persistData)}
                ref={form}
            >
                <Grid
                    container
                    direction='column'
                    justifyContent='flex-start'
                    alignItems='stretch'
                    spacing={2}
                >
                    {gridify ? applyGrid(props.children) : props.children}
                </Grid>
            </form>
        </FormProvider>
    );
};

export default EndSection;
